import { inject, Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivateFn,
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Route,
  Router,
  RouterStateSnapshot,
  Routes,
} from '@angular/router';
import { Observable } from 'rxjs';
import { Title } from '@angular/platform-browser';
import {
  SolApp,
  SolNavbar,
  SolNavbarItem,
  SolNavigationOptions,
  SolRoute,
  SolRouteId,
  SolRouteType,
  SolTopbarData,
} from '../models/sol-navigation';
import { SolMenuGroup } from '../components/popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { AuthorizationService, SolAuthority, SolRequiredAuthorities } from './authorization.service';
import { NavigationTarget, RouterTools, UpdateQueryParamsOptions } from '../tools/router-tools';
import { AuthenticationService } from './authentication.service';
import { AbstractService } from './abstract-service';
import { LocaleService } from './locale.service';
import { PreferenceService } from './preference.service';
import { SolSettingKey } from '../models/core/sol-setting-key';
import { ObjectTools } from '../tools/object-tools';
import { AuthorizationTools } from '../tools/authorization-tools';
import { DebugService } from './debug.service';
import { SolTag } from '../models/core/sol-tag';
import { EmbedService } from './embed.service';
import { BrowserTools } from '../tools/browser-tools';

export interface NavigationState {
  appId: SolRouteId | undefined;
  apps: SolApp[];
  routes: SolRoute[];
  activeRouteId: SolRouteId | undefined;
  activeRoute: SolRoute | undefined;
  navbarVisible: boolean;
  /**
   * A hidden navbar is slid open over the content. Interaction state, not a preference: the
   * topbar's menu button drives it, and it is meaningless while the navbar is docked.
   */
  navbarDrawerOpen: boolean;
  /**
   * The app-context query params ({@link SolApp.contextParams} of every app) of the confirmed URL.
   * Route params belong to the page that renders them and are read through `SolUrlHandler`; this is
   * the app-level slice the session owns instead.
   */
  appContext: Record<string, string>;
  /**
   * The query params each route was last left with, keyed by `SolRouteId`, so going back up to a list
   * reopens it as the user left it. Read through {@link SolNavigationOptions.restoreQueryParams}.
   */
  lastQueryParams: Record<string, Record<string, string>>;
  navigating: boolean;
  activeRouteStatus: SolActiveRouteStatus;
  /**
   * Runtime tag overlay, keyed by `SolRouteId`. When a navbar item is rendered as a tab,
   * the tag is applied to the generated `SolTab.tag` (e.g. a count badge).
   * Set via {@link NavigationService.setNavbarItemTag}; not persisted across sessions.
   */
  navbarItemTags: Record<string, SolTag>;
  /**
   * Runtime visibility overlay, keyed by `SolRouteId`. When a navbar item is rendered as a
   * tab, an entry set to `true` hides it from the topbar tab strip. Set via
   * {@link NavigationService.setNavbarItemHidden}; not persisted across sessions.
   */
  navbarItemHidden: Record<string, boolean>;
  /** Runtime count badges, keyed by route id. A count of `0` renders nothing. */
  navbarItemCounts: Record<string, number>;

  // Computed
  activeNavbarItems: SolNavbarItem[];
  navbar: SolNavbar;
  pageNameParams: Record<string, any>;
  topbarData: SolTopbarData;
  appMenu: SolMenuGroup[];
  showNavbar: boolean;

  // Private
  _authorities: SolAuthority[];
  _variables: Record<string, any>;
}

export type SolActiveRouteStatus = 'loading' | 'forbidden' | 'allowed' | 'error';

export const AuthGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
): Observable<boolean> => {
  const authorizationService = inject(AuthorizationService);
  return authorizationService.checkRouteAccess(next);
};

export const AuthLogoutGuard: CanActivateFn = (_next: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean => {
  const authenticationService = inject(AuthenticationService);
  authenticationService.logout();
  return false;
};

export const AuthProfileGuard: CanActivateFn = (
  _next: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
): boolean => {
  const authenticationService = inject(AuthenticationService);
  authenticationService.goToProfile();
  return false;
};

export const AuthDebugGuard: CanActivateFn = (_next: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean => {
  const debugService = inject(DebugService, { optional: true });
  debugService?.toggleVisible();
  return false;
};

@Injectable({
  providedIn: 'root',
})
export class NavigationService extends AbstractService<NavigationState> {
  // Dependencies
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly title = inject(Title);
  private readonly localeService = inject(LocaleService);
  private readonly embedService = inject(EmbedService);
  private readonly preferenceService = inject(PreferenceService);

  constructor() {
    super(
      {
        appId: undefined,
        apps: [],
        routes: [],
        activeRouteId: undefined,
        navbarVisible: true,
        navbarDrawerOpen: false,
        appContext: {},
        lastQueryParams: {},
        navigating: false,
        activeRouteStatus: 'loading',
        navbarItemTags: {},
        navbarItemHidden: {},
        navbarItemCounts: {},

        // Computed
        activeRoute: undefined,
        activeNavbarItems: [],
        navbar: {
          menu: [],
          system: [],
          account: [],
        },
        pageNameParams: {},
        topbarData: {
          tabs: [],
          selectedTab: '',
          breadcrumbs: [],
          pageNamesLoaded: false,
        },
        appMenu: [],
        showNavbar: false,

        // Variables
        _authorities: [],
        _variables: {},
      },
      [
        NavigationService.calculateAuthorities,
        NavigationService.calculateActiveRoute,
        NavigationService.calculateMenus,
        NavigationService.calculateActiveNavbarItems,
        NavigationService.calculateTopbarData,
        NavigationService.calculateShowNavbar,
      ],
    );

    // Single router listener to capture navigation lifecycle
    this.router.events.subscribe(event => this.onRouterEvent(event));

    // Watch auth fetched state to update activeRouteStatus
    this.authorizationService.watch(() => this.updateActiveRouteStatus());

    // Watch authorities to recalculate route access
    this.authorizationService.watchComputed(
      state => state.authorities,
      authorities => {
        this.onAuthorizationChanged(authorities);
      },
    );

    // Update page title
    this.localeService.watchComputed(
      lss => lss.locale,
      () => this.onLocaleChanged(),
    );
    this.watchComputed(
      nss => [nss.activeRouteId, nss.activeRouteStatus, nss.appId, nss.pageNameParams],
      () => this.onPageTitleDependenciesChanged(),
    );
  }

  public initialize(apps: SolApp[], ngRoutes: Routes, _variables: Record<string, any>): void {
    const routes: SolRoute[] = [];
    for (const ngRoute of ngRoutes) {
      // Convert Angular route to SolRoute
      const route = NavigationService.toSolRoute(ngRoute);
      if (route === undefined) {
        continue;
      }

      // Add to results
      routes.push(route);
    }
    this.patch({ apps, routes, _variables });
    this.fetchNavbarVisible();
  }

  public navigateHome(): void {
    const routes = this.state.routes;
    const homeRouteId = routes.find(route => route.path === '')?.id ?? ObjectTools.findFirst(routes)?.id;
    if (homeRouteId !== undefined) {
      this.navigate(homeRouteId);
    }
  }

  public navigate(routeId: SolRouteId, options?: SolNavigationOptions): void {
    const state = this.state;
    const route = RouterTools.findRoute(state, routeId);
    if (route === undefined) {
      return;
    }
    const queryParams = options?.restoreQueryParams
      ? { ...this.restorableQueryParams(routeId), ...options.queryParams }
      : options?.queryParams;

    // Embedded, the host owns every page it has a url for: it mounts an Angular embed on a few
    // paths only and cannot route the rest, so an internal navigation would strand it.
    if ((options?.toVueApp || route.vueOnly || BrowserTools.isEmbedded()) && route.vueUrl) {
      const target = options?.target ?? NavigationTarget.SameTab;
      const pathParams = { ...this.currentUrlPathParams(), ...(options?.pathParams ?? {}) };
      const fullUrl = RouterTools.calculateInternalUrl(
        RouterTools.calculateExternalUrl(route.vueUrl, state._variables),
        pathParams,
      );
      if (BrowserTools.isEmbedded()) {
        // The Vue host expects a path-only hostPath (starts with "/").
        // vueUrl resolves to a full URL whose origin matches the Vue host's own
        // origin, so stripping to pathname is correct.
        this.embedService.navigateHost(new URL(fullUrl).pathname, queryParams as any);
      } else {
        const externalUrl = NavigationService.toUrlWithQueryParams(fullUrl, queryParams);
        RouterTools.goToExternal(externalUrl, target);
      }
      return;
    }

    if (route.type === SolRouteType.Internal) {
      const pathParams = { ...this.currentUrlPathParams(), ...(options?.pathParams ?? {}) };
      const url = RouterTools.calculateInternalUrl(route.path, pathParams);
      const target = options?.target ?? NavigationTarget.SameTab;
      const contextParams = (RouterTools.findApp(state, routeId) ?? RouterTools.findCurrentApp(state))?.contextParams;
      const routerQueryParams = {
        ...NavigationService.pickParams(this.currentUrlQueryParams(), contextParams ?? []),
        ...(queryParams ?? {}),
      };
      const fragment = options?.fragment;
      if (target === NavigationTarget.NewTab) {
        const path = this.router.serializeUrl(
          this.router.createUrlTree([url], {
            fragment,
            queryParams: routerQueryParams,
          }),
        );
        RouterTools.goToExternal(path, target);
      } else {
        this.router.navigate([url], {
          fragment,
          queryParams: routerQueryParams,
        });
      }
    } else {
      const target = options?.target ?? NavigationTarget.NewTab;
      const path = RouterTools.calculateExternalUrl(route.path, state._variables);
      const externalUrl = NavigationService.toUrlWithQueryParams(path, queryParams);
      RouterTools.goToExternal(externalUrl, target);
    }
  }

  public navigateByUrl(url: string, options?: SolNavigationOptions): void {
    const finalUrl = NavigationService.toUrlWithQueryParams(url, options?.queryParams);
    const target = options?.target || NavigationTarget.NewTab;
    RouterTools.goToExternal(finalUrl, target);
  }

  /**
   * Navigate to an absolute Angular path that does not have a registered route ID.
   * Used by host-bridge inbound handlers that receive raw paths from the Vue host.
   * Standalone-equivalent of router.navigate([path], { queryParams }) but routed
   * through NavigationService so downstream NavigationState stays in sync.
   */
  public navigateByPath(path: string, queryParams?: Record<string, unknown>): Promise<boolean> {
    return this.router.navigate([path], { queryParams });
  }

  public fetchNavbarVisible(): void {
    const cached = this.preferenceService.cached(SolSettingKey.navbarVisible);
    if (cached !== undefined) {
      this.patch({ navbarVisible: cached });
    }
    this.preferenceService.load(SolSettingKey.navbarVisible).subscribe(navbarVisible => {
      if (navbarVisible !== undefined) {
        this.patch({ navbarVisible });
      }
    });
  }

  /**
   * Slides a hidden navbar in over the content, or back out. Nothing is persisted: this is where
   * the pointer is, not what the user chose, and it is ignored while the navbar is docked.
   */
  public updateNavbarDrawerOpen(navbarDrawerOpen: boolean): void {
    if (this.state.navbarVisible || this.state.navbarDrawerOpen === navbarDrawerOpen) {
      return;
    }
    this.patch({ navbarDrawerOpen });
  }

  /**
   * Updates the navbar visible state and persists it. The navbar is chrome with no page and no panel
   * to carry an alert, so this is the one preference write whose failure is announced as a toast.
   */
  public updateNavbarVisible(visible: boolean): void {
    const previous = this.state.navbarVisible;
    const previousDrawerOpen = this.state.navbarDrawerOpen;
    // Hiding from under the pointer would yank the navbar away mid-click, so it stays slid
    // open and the usual mouseleave closes it once the pointer has moved on.
    this.patch({ navbarVisible: visible, navbarDrawerOpen: !visible });
    this.preferenceService.save(SolSettingKey.navbarVisible, visible, { announcedAsToast: true }).subscribe({
      // Both fields were patched together, so both roll back together: restoring only the mode
      // would leave a docked navbar carrying an open drawer.
      error: () => this.patch({ navbarVisible: previous, navbarDrawerOpen: previousDrawerOpen }),
    });
  }

  /**
   * Set the translation page name params (see LocaleService).
   */
  public setPageNameParams(pageNameParams: Record<string, any>): void {
    this.patch({ pageNameParams });
  }

  /**
   * Set a tag (badge) on a navbar item, applied at render time when the item is shown
   * as a tab. Pass `undefined` to clear. Pages typically call this when they load data
   * whose count should appear on a tab, e.g. a "3" badge next to "Comments".
   */
  public setNavbarItemTag(routeId: SolRouteId, tag: SolTag | undefined): void {
    const current = this.state.navbarItemTags;
    if (ObjectTools.equals(current[routeId], tag)) return;
    const next = { ...current };
    if (tag === undefined) {
      delete next[routeId];
    } else {
      next[routeId] = tag;
    }
    this.patch({ navbarItemTags: next });
  }

  /**
   * Hide or show a navbar item that renders as a topbar tab. Pass `true` to hide, `false`
   * (or `undefined`) to clear the override. Pages call this when a tab is irrelevant for
   * the loaded entity, e.g. hiding "Comments" for a draft engagement request.
   */
  public setNavbarItemHidden(routeId: SolRouteId, hidden: boolean): void {
    const current = this.state.navbarItemHidden;
    if ((current[routeId] ?? false) === hidden) return;
    const next = { ...current };
    if (hidden) {
      next[routeId] = true;
    } else {
      delete next[routeId];
    }
    this.patch({ navbarItemHidden: next });
  }

  /** Sets the badge count for a route id. `0` clears it, so callers never decide visibility. */
  public setNavbarItemCount(routeId: SolRouteId, count: number): void {
    const current = this.state.navbarItemCounts;
    if ((current[routeId] ?? 0) === count) return;
    this.patch({ navbarItemCounts: { ...current, [routeId]: count } });
  }

  /**
   * Update the URL's query string as a PATCH (merge) or PUT (replace-all).
   *
   * - `mode: 'patch'` (default) merges into current query params.
   * - `mode: 'put'` replaces the entire set (keys not provided are removed).
   * - `null`/`undefined` removes a key. Every other value is written, an empty string included: a key
   *   present with no value is a state of its own, which the page that owns it reads back as such.
   * - Arrays become `string[]`; Dates become ISO strings; objects are JSON stringified.
   * - Uses `replaceUrl: true` to avoid spamming history; preserves fragment by default.
   *
   * @example
   * // PATCH: set tab=details and remove page
   * RouterTools.updateQueryParams(router, route, { tab: 'details', page: null });
   *
   * @example
   * // PUT: only keep ?q=hello
   * RouterTools.updateQueryParams(router, route, { q: 'hello' }, { mode: 'put' });
   */
  public updateQueryParams(
    params: Record<string, any> | undefined,
    options: Partial<UpdateQueryParamsOptions> = {},
  ): void {
    if (params === undefined || this.state.navigating) {
      return;
    }

    const mode = options?.mode ?? 'patch';
    const current: Record<string, any> = mode === 'patch' ? { ...this.currentUrlQueryParams() } : {};
    const next: Record<string, any> = { ...current };

    for (const [key, value] of Object.entries(params)) {
      // Deletions
      if (value === undefined || value === null) {
        delete next[key];
        continue;
      }

      // Normalize
      if (value instanceof Date) {
        next[key] = value.toISOString();
      } else if (Array.isArray(value)) {
        next[key] = value.map(v => String(v));
      } else if (typeof value === 'object') {
        // Explicit + reversible
        next[key] = JSON.stringify(value);
      } else {
        next[key] = value;
      }
    }

    // Remove any leftover nullish assignments (defensive)
    for (const k of Object.keys(next)) {
      if (next[k] == null) delete next[k];
    }

    if (NavigationService.areQueryParamsEqual(current, next)) {
      return;
    }

    void this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: next,
        replaceUrl: options?.replaceUrl ?? true,
        preserveFragment: options?.preserveFragment ?? true,
      })
      .catch(error => {
        console.error('Failed to update query params', error);
      });
  }

  private currentUrlQueryParams(): Record<string, string> {
    return this.activatedRoute.snapshot.queryParams as Record<string, string>;
  }

  private currentUrlPathParams(): Record<string, string> {
    return NavigationService.collectPathParams(this.router.routerState.snapshot.root);
  }

  /** The page-local params `routeId` was last left with, or none once the app context has changed since. */
  private restorableQueryParams(routeId: SolRouteId): Record<string, string> {
    const last = this.state.lastQueryParams[routeId];
    if (last === undefined || !ObjectTools.equals(this.pickAppContext(last), this.state.appContext)) {
      return {};
    }
    const contextKeys = this.state.apps.flatMap(app => app.contextParams);
    return Object.fromEntries(Object.entries(last).filter(([key]) => !contextKeys.includes(key)));
  }

  private pickAppContext(params: Record<string, string>): Record<string, string> {
    return NavigationService.pickParams(
      params,
      this.state.apps.flatMap(app => app.contextParams),
    );
  }

  private static pickParams(params: Record<string, string>, keys: readonly string[]): Record<string, string> {
    const picked: Record<string, string> = {};
    for (const key of keys) {
      if (params[key] !== undefined) {
        picked[key] = params[key];
      }
    }
    return picked;
  }

  private onRouterEvent(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.patch({ navigating: true });
      return;
    }
    if (event instanceof NavigationError) {
      this.patch({ navigating: false });
      this.setActiveRouteStatus('error');
      return;
    }
    if (event instanceof NavigationCancel) {
      this.patch({ navigating: false });
      return;
    }
    if (event instanceof NavigationEnd) {
      this.applyRouteInfo(this.activatedRoute.snapshot.root);
      this.updateActiveRouteStatus();
      this.patch({ navigating: false });
    }
  }

  private onAuthorizationChanged(authorities: SolAuthority[]): void {
    this.patch({ _authorities: authorities });
  }

  private onLocaleChanged(): void {
    this.updatePageTitle();
  }

  private onPageTitleDependenciesChanged(): void {
    this.updatePageTitle();
  }

  private updateActiveRouteStatus(): void {
    if (!this.authorizationService.state.fetched || this.state.activeRouteId === undefined) {
      this.setActiveRouteStatus('loading');
      return;
    }

    const activeRoute = RouterTools.findRoute(this.state, this.state.activeRouteId);
    if (activeRoute === undefined) {
      this.setActiveRouteStatus('error');
      return;
    }

    const hasRouteAccess = AuthorizationTools.hasAuthority(
      this.authorizationService.state.authorities,
      activeRoute.authorities,
    );
    this.setActiveRouteStatus(hasRouteAccess ? 'allowed' : 'forbidden');
  }

  private updatePageTitle(): void {
    const state = this.state;
    const appName = RouterTools.findCurrentApp(state)?.name;
    if (!appName || !this.localeService.state.translations) {
      return;
    }

    const breadcrumb = ObjectTools.findLast(state.topbarData.breadcrumbs);
    const pageName = state.topbarData.pageNamesLoaded ? breadcrumb?.name?.withParams(state.pageNameParams) : undefined;
    const pageNameTranslated = this.localeService.translateText(pageName);
    this.title.setTitle(appName + (!!pageNameTranslated ? ' - ' + pageNameTranslated : ''));
  }

  private setActiveRouteStatus(activeRouteStatus: SolActiveRouteStatus): void {
    this.patch({ activeRouteStatus });
  }

  /** Publishes the active route and app context after navigation completes. */
  private applyRouteInfo(snapshot: ActivatedRouteSnapshot, activeRouteStatus?: SolActiveRouteStatus): void {
    const leaf = NavigationService.findLeaf(snapshot);
    const data = leaf.routeConfig?.data ?? {};
    const activeRouteId = (data['id'] as SolRouteId | undefined) ?? undefined;
    const queryParams = snapshot.queryParams as Record<string, string>;
    const appContext = this.pickAppContext(queryParams);
    const appId =
      (activeRouteId !== undefined ? RouterTools.findApp(this.state, activeRouteId)?.id : undefined) ??
      this.state.appId ??
      RouterTools.findAccessibleApp(this.state)?.id;
    if (activeRouteId !== undefined && RouterTools.findRoute(this.state, activeRouteId) === undefined) {
      console.warn('NavigationService: no route found for activeRouteId', activeRouteId);
    }
    this.update(state => ({
      activeRouteId: activeRouteId ?? state.activeRouteId,
      appId,
      appContext,
      lastQueryParams:
        activeRouteId !== undefined
          ? { ...state.lastQueryParams, [activeRouteId]: queryParams }
          : state.lastQueryParams,
      activeRouteStatus: activeRouteStatus ?? state.activeRouteStatus,
    }));
  }

  private static calculateAuthorities(state: NavigationState, previous: NavigationState): NavigationState {
    // Skip if authorities and routes haven't changed
    if (state._authorities === previous._authorities && state.routes === previous.routes) {
      return state;
    }

    const authorities = state._authorities;
    const routes = state.routes.map(route => {
      const hasAuthority = AuthorizationTools.hasAuthority(authorities, route.authorities);
      return { ...route, hasAuthority };
    });
    const apps = state.apps.map(app =>
      app.withComputedAuthorities(AuthorizationTools.hasAuthority(authorities, app.authorities)),
    );
    const resolved = { ...state, routes, apps };
    return { ...resolved, appId: resolved.appId ?? RouterTools.findAccessibleApp(resolved)?.id };
  }

  private static calculateMenus(nss: NavigationState): NavigationState {
    const currentNavbar =
      RouterTools.findCurrentApp(nss)?.navbar ?? RouterTools.findAccessibleApp(nss)?.navbar ?? nss.navbar;
    const menu = RouterTools.computeNavbarItems(nss, currentNavbar.menu);
    const system = RouterTools.computeNavbarItems(nss, currentNavbar.system);
    const account = RouterTools.computeNavbarItems(nss, currentNavbar.account);
    const navbar = { menu, system, account };
    const appMenu = RouterTools.computeAppMenu(nss);
    return { ...nss, navbar, appMenu };
  }

  private static areQueryParamsEqual(current: Record<string, any>, next: Record<string, any>): boolean {
    const currentKeys = Object.keys(current);
    const nextKeys = Object.keys(next);
    if (currentKeys.length !== nextKeys.length) {
      return false;
    }
    return currentKeys.every(key => current[key] === next[key]);
  }

  private static calculateActiveNavbarItems(nss: NavigationState): NavigationState {
    const activeNavbarItems = RouterTools.findActiveNavbarItems(nss.navbar, nss.activeRouteId);
    return { ...nss, activeNavbarItems };
  }

  private static calculateTopbarData(nss: NavigationState): NavigationState {
    const topbarData = RouterTools.toTopbarData(nss);
    return { ...nss, topbarData };
  }

  private static calculateActiveRoute(nss: NavigationState): NavigationState {
    const activeRoute = RouterTools.findRoute(nss, nss.activeRouteId);
    return { ...nss, activeRoute };
  }

  private static calculateShowNavbar(nss: NavigationState): NavigationState {
    const routeShowNavbar = nss.activeRoute?.showNavbar ?? true;
    const showNavbar = routeShowNavbar;
    return { ...nss, showNavbar };
  }

  private static toSolRoute(ngRoute: Route): SolRoute | undefined {
    if (!ngRoute.data) {
      return undefined;
    }

    const id: SolRouteId | undefined = ngRoute.data['id'] as SolRouteId;
    if (id === undefined) {
      console.warn('NavigationService: route missing data.id', ngRoute);
      return undefined;
    }

    const externalUrl = ngRoute.data['externalUrl'] as string;
    const external = !!externalUrl || ((ngRoute.data['external'] as boolean) ?? false);
    const type = external ? SolRouteType.External : SolRouteType.Internal;
    const vueUrl = ngRoute.data['vueUrl'] as string;
    const vueOnly = (ngRoute.data['vueOnly'] as boolean) ?? false;
    const path = type === SolRouteType.External ? externalUrl : ngRoute.path!;
    const goTo = (ngRoute.data['goTo'] as () => void) || undefined;
    const app = ngRoute.data['app'] as SolRouteId | undefined;
    const authorities = (ngRoute.data['authorities'] as SolRequiredAuthorities) || [];
    const showNavbar = (ngRoute.data['showNavbar'] as boolean) ?? true;
    const hasAuthority = false;
    const isRedirect = !!ngRoute.redirectTo;
    return {
      id,
      type,
      path,
      vueUrl,
      vueOnly,
      goTo,
      app,
      authorities,
      hasAuthority,
      showNavbar,
      isRedirect,
    };
  }

  private static toUrlWithQueryParams(path: string, queryParams: Record<string, string> = {}): string {
    const queryKeys = Object.keys(queryParams);
    const queryString = queryKeys
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&');
    return queryKeys.length > 0 ? `${path}?${queryString}` : path;
  }

  private static collectPathParams(snapshot: ActivatedRouteSnapshot): Record<string, string> {
    const params: Record<string, string> = {};
    let node: ActivatedRouteSnapshot | null = snapshot;
    while (node) {
      Object.assign(params, node.params as Record<string, string>);
      node = node.firstChild ?? null;
    }
    return params;
  }

  private static findLeaf(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let node: ActivatedRouteSnapshot = snapshot;
    while (node.firstChild) {
      node = node.firstChild;
    }
    return node;
  }
}
